(() => {
  'use strict';
  const elementName = 'kiekkokingi-restock-archive';
  if (customElements.get(elementName)) return;

  class KiekkoKingiRestockArchive extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.automatic = this.dataset.automaticRestocks !== 'false';
      this.timeZone = this.dataset.timeZone || 'Europe/Helsinki';
      const hour = Number.parseInt(this.dataset.releaseHour, 10);
      this.releaseHour = Number.isFinite(hour) ? Math.min(23, Math.max(0, hour)) : 11;
      this.releaseMinute = this.numberFromData('releaseMinute', 0, 0, 59);
      this.linkDaysBefore = this.numberFromData('linkDaysBefore', this.automatic ? 1 : 0, 0, 6);
      this.linkHour = this.numberFromData('linkHour', this.automatic ? 16 : this.releaseHour, 0, 23);
      this.linkMinute = this.numberFromData('linkMinute', 0, 0, 59);
      this.upcomingLabel = this.dataset.upcomingLabel || 'TULOSSA';
      this.upcomingCardLabel = this.dataset.upcomingCardLabel || 'Tulossa pian';
      this.latestLabel = this.dataset.latestLabel || 'UUSIN RESTOCK';
      this.waitingText = this.dataset.waitingText || 'Restock julkaistaan pian.';
      this.countdown = this.querySelector('[data-restock-countdown]');
      this.status = this.querySelector('[data-featured-status]');
      this.featuredCta = this.querySelector('[data-featured-cta]');
      this.featuredCopy = this.querySelector('[data-featured-copy]');
      this.dateLabel = this.querySelector('[data-featured-date-label]');
      this.releaseTimes = new Map();
      this.linkTimes = new Map();
      this.refreshAttempts = 0;
      this.handleClick = this.onClick.bind(this);
      this.handleVisibility = this.onVisibilityChange.bind(this);
      this.addEventListener('click', this.handleClick);
      document.addEventListener('visibilitychange', this.handleVisibility);
      try {
        this.partsFormatter = new Intl.DateTimeFormat('en-CA', {
          timeZone: this.timeZone,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
        });
        this.readSchedule(this);
        this.collectArchive();
        this.update();
        this.startTimer();
      } catch {
        // Keep the verified server-rendered links if browser date support fails.
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this.stopTimer();
      this.cancelRefresh();
      this.initialized = false;
      this.calendarDay = undefined;
      this.refreshKey = undefined;
      this.successfulRefreshKey = undefined;
    }

    parseDate(value) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
      const [year, month, day] = value.split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day));
      if (date.getUTCFullYear() !== year || date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day) return null;
      return { year, month, day };
    }

    numberFromData(key, fallback, minimum, maximum) {
      const value = Number.parseInt(this.dataset[key], 10);
      return Number.isFinite(value) ? Math.min(maximum, Math.max(minimum, value)) : fallback;
    }

    dateKey({ year, month, day }) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    partsAt(date) {
      const values = {};
      this.partsFormatter.formatToParts(date).forEach((part) => {
        if (part.type !== 'literal') values[part.type] = Number.parseInt(part.value, 10);
      });
      return values;
    }

    wallTimeToDate(year, month, day, hour, minute = 0) {
      const desired = Date.UTC(year, month - 1, day, hour, minute, 0);
      let instant = desired;
      for (let attempt = 0; attempt < 5; attempt += 1) {
        const actual = this.partsAt(new Date(instant));
        const correction = desired - Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, actual.second);
        instant += correction;
        if (correction === 0) break;
      }
      return new Date(instant);
    }

    releaseAt(key) {
      if (!this.releaseTimes.has(key)) {
        const date = this.parseDate(key);
        this.releaseTimes.set(key, date ? this.wallTimeToDate(date.year, date.month, date.day, this.releaseHour, this.releaseMinute).getTime() : Infinity);
      }
      return this.releaseTimes.get(key);
    }

    linkAt(key) {
      if (!this.linkTimes.has(key)) {
        const date = this.parseDate(key);
        let instant = Infinity;
        if (date) {
          // Shift the calendar day before converting to Helsinki wall time:
          // Saturday and Sunday may have different daylight-saving offsets.
          const activation = new Date(Date.UTC(date.year, date.month - 1, date.day - this.linkDaysBefore));
          instant = this.wallTimeToDate(
            activation.getUTCFullYear(), activation.getUTCMonth() + 1, activation.getUTCDate(), this.linkHour, this.linkMinute,
          ).getTime();
          instant = Math.min(instant, this.releaseAt(key));
        }
        this.linkTimes.set(key, instant);
      }
      return this.linkTimes.get(key);
    }

    scheduleAt(now) {
      if (!this.automatic) return this.dataset.featuredDate;
      const local = this.partsAt(now);
      const today = new Date(Date.UTC(local.year, local.month - 1, local.day));
      // Sunday's release stays featured until Monday, matching the home countdown.
      today.setUTCDate(today.getUTCDate() + (7 - today.getUTCDay()) % 7);
      return this.dateKey({ year: today.getUTCFullYear(), month: today.getUTCMonth() + 1, day: today.getUTCDate() });
    }

    readSchedule(root) {
      const node = root.querySelector('[data-restock-schedule]');
      const entries = JSON.parse(node?.textContent || '[]');
      if (!Array.isArray(entries)) throw new Error('Invalid restock schedule');
      this.entries = new Map();
      entries.forEach((entry) => {
        if (this.parseDate(entry.date) && typeof entry.handle === 'string' && !this.entries.has(entry.date)) {
          this.entries.set(entry.date, { ...entry, available: entry.available === true });
        }
      });
      this.renderedAt = Number(root.dataset.renderedAt) * 1000 || 0;
    }

    verifiedUrl(entry) {
      if (!entry?.available || !entry.url) return null;
      try {
        const url = new URL(entry.url, location.origin);
        const prefix = new URL(this.dataset.collectionsUrl || '/collections', location.origin).pathname.replace(/\/$/, '') + '/';
        return url.origin === location.origin && url.pathname.startsWith(prefix) ? url.pathname + url.search : null;
      } catch {
        return null;
      }
    }

    latestEligibleUrl(now) {
      let latestDate = '';
      let latestUrl = null;
      this.entries.forEach((entry, date) => {
        const url = this.verifiedUrl(entry);
        if (url && date > latestDate && this.linkAt(date) <= now) {
          latestDate = date;
          latestUrl = url;
        }
      });
      return latestUrl;
    }

    collectArchive() {
      this.filterButtons = Array.from(this.querySelectorAll('[data-year-filter]'));
      this.yearSections = Array.from(this.querySelectorAll('[data-restock-year-section]'));
      this.items = Array.from(this.querySelectorAll('[data-restock-item]'));
      this.emptyState = this.querySelector('[data-restock-empty]');
    }

    onClick(event) {
      const button = event.target.closest('[data-year-filter]');
      if (!button || !this.contains(button)) return;
      this.userSelectedFilter = true;
      this.activeFilter = button.dataset.yearFilter;
      this.updateArchive(Date.now());
    }

    updateArchive(now) {
      const visibleYears = new Set();
      let latestDate = '';
      this.items.forEach((item) => {
        const date = item.dataset.restockDate;
        item.hidden = this.linkAt(date) > now;
        if (!item.hidden) {
          visibleYears.add(item.dataset.restockYear || date.slice(0, 4));
          if (this.releaseAt(date) <= now && date > latestDate) latestDate = date;
        }
      });
      if (!this.userSelectedFilter || !this.activeFilter || (this.activeFilter !== 'all' && !visibleYears.has(this.activeFilter))) {
        this.activeFilter = [...visibleYears].sort().reverse()[0] || 'all';
      }
      const selected = this.filterButtons.length ? this.activeFilter : 'all';
      this.filterButtons.forEach((button) => {
        const year = button.dataset.yearFilter;
        button.hidden = year !== 'all' && !visibleYears.has(year);
        button.setAttribute('aria-pressed', String(year === selected));
      });
      this.yearSections.forEach((section) => {
        const year = section.dataset.restockYearSection;
        section.hidden = !visibleYears.has(year) || (selected !== 'all' && selected !== year);
      });
      this.items.forEach((item) => {
        const badge = item.querySelector('[data-restock-current-badge]');
        if (badge) {
          const upcoming = this.releaseAt(item.dataset.restockDate) > now;
          badge.hidden = item.hidden || (!upcoming && item.dataset.restockDate !== latestDate);
          badge.textContent = upcoming ? this.upcomingCardLabel : this.latestLabel;
        }
        const count = item.querySelector('[data-restock-current-count], [data-restock-card-count]');
        if (count) count.textContent = item.dataset.restockPublishedText || count.dataset.publishedText || '';
      });
      if (this.emptyState) this.emptyState.hidden = visibleYears.size > 0;
    }

    update() {
      if (!this.isConnected || !this.partsFormatter) return;
      const now = new Date();
      const today = this.dateKey(this.partsAt(now));
      const key = this.scheduleAt(now);
      const date = this.parseDate(key);
      if (!date) return;
      const changedDay = this.calendarDay !== undefined && this.calendarDay !== today;
      this.calendarDay = today;
      this.targetKey = key;
      const release = this.releaseAt(key);
      const entry = this.entries.get(key);
      const url = this.verifiedUrl(entry);
      const published = now.getTime() >= release;
      const linkOpen = now.getTime() >= this.linkAt(key);
      const ctaUrl = this.automatic ? this.latestEligibleUrl(now.getTime()) : published ? url : null;
      if (this.dateLabel) this.dateLabel.textContent = `${this.dataset.datePrefix || 'Sunnuntai'} ${date.day}.${date.month}.${date.year}`;
      if (this.status) this.status.textContent = published && url ? this.latestLabel : this.upcomingLabel;
      if (this.featuredCta) {
        this.featuredCta.hidden = !ctaUrl;
        if (ctaUrl) this.featuredCta.setAttribute('href', ctaUrl);
        else this.featuredCta.removeAttribute('href');
      }
      if (this.featuredCopy) {
        this.featuredCopy.hidden = !published;
        this.featuredCopy.textContent = url ? this.dataset.featuredPublishedText || '' : this.waitingText;
      }
      if (this.countdown) {
        this.countdown.hidden = published;
        const remaining = Math.max(0, Math.ceil((release - now.getTime()) / 1000));
        this.setCountdownValue('days', Math.floor(remaining / 86400));
        this.setCountdownValue('hours', Math.floor((remaining % 86400) / 3600));
        this.setCountdownValue('minutes', Math.floor((remaining % 3600) / 60));
        this.setCountdownValue('seconds', remaining % 60);
      }
      this.updateArchive(now.getTime());
      const phase = published ? 'published' : linkOpen ? 'link-open' : 'before-link';
      const refreshKey = `${today}|${key}|${phase}`;
      const staleLink = linkOpen && this.renderedAt < this.linkAt(key) && this.successfulRefreshKey !== refreshKey;
      const staleRelease = published && this.renderedAt < release && this.successfulRefreshKey !== refreshKey;
      if (changedDay || !entry || (linkOpen && !url) || staleLink || staleRelease) this.requestRefresh(refreshKey);
    }

    setCountdownValue(unit, value) {
      const element = this.querySelector(`[data-countdown-value="${unit}"]`);
      if (element) element.textContent = String(value).padStart(2, '0');
    }

    requestRefresh(key) {
      if (document.hidden || window.Shopify?.designMode || !this.dataset.sectionId) return;
      if (this.refreshKey !== key) {
        this.cancelRefresh();
        this.refreshKey = key;
        this.refreshAttempts = 0;
      }
      if (this.refreshPending || this.retryTimer || this.refreshAttempts >= 4) return;
      this.refreshArchive(key);
    }

    cancelRefresh() {
      this.refreshController?.abort();
      this.refreshController = null;
      clearTimeout(this.retryTimer);
      clearTimeout(this.requestTimeout);
      this.retryTimer = null;
      this.refreshPending = false;
    }

    async refreshArchive(key) {
      this.refreshPending = true;
      this.refreshAttempts += 1;
      const controller = new AbortController();
      this.refreshController = controller;
      this.requestTimeout = window.setTimeout(() => controller.abort(), 8000);
      let succeeded = false;
      try {
        const url = new URL(location.href);
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('_kk_restock_refresh', String(Date.now()));
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Restock refresh failed');
        const html = await response.text();
        if (!this.isConnected || controller.signal.aborted || this.refreshKey !== key) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = Array.from(doc.querySelectorAll(elementName)).find((root) => root.id === this.id);
        const oldArchive = this.querySelector('[data-restock-archive-content]');
        const newArchive = incoming?.querySelector('[data-restock-archive-content]');
        if (!incoming || !oldArchive || !newArchive || !incoming.querySelector('[data-restock-schedule]')) throw new Error('Invalid restock response');
        const focused = document.activeElement;
        const focusedYear = oldArchive.contains(focused) ? focused.dataset.yearFilter : null;
        const focusedDate = oldArchive.contains(focused) ? focused.closest('[data-restock-item]')?.dataset.restockDate : null;
        this.readSchedule(incoming);
        const scheduleNode = this.querySelector('[data-restock-schedule]');
        scheduleNode.textContent = incoming.querySelector('[data-restock-schedule]').textContent;
        this.dataset.renderedAt = incoming.dataset.renderedAt;
        // Keep newsletter inputs, messages and listeners intact during publication.
        oldArchive.innerHTML = newArchive.innerHTML;
        this.collectArchive();
        this.successfulRefreshKey = key;
        succeeded = true;
        this.update();
        if (focusedYear) this.filterButtons.find((button) => button.dataset.yearFilter === focusedYear)?.focus({ preventScroll: true });
        else if (focusedDate) this.items.find((item) => item.dataset.restockDate === focusedDate)?.querySelector('a')?.focus({ preventScroll: true });
      } catch {
        // Leave the last valid archive and countdown in place on network errors.
      } finally {
        if (this.refreshController !== controller) return;
        clearTimeout(this.requestTimeout);
        this.refreshController = null;
        this.refreshPending = false;
        const waiting = Date.now() >= this.linkAt(this.targetKey) && !this.verifiedUrl(this.entries.get(this.targetKey));
        if (this.isConnected && !document.hidden && this.refreshKey === key && (!succeeded || waiting) && this.refreshAttempts < 4) {
          const delay = [5000, 30000, 120000][this.refreshAttempts - 1];
          this.retryTimer = window.setTimeout(() => {
            this.retryTimer = null;
            this.requestRefresh(key);
          }, delay);
        }
      }
    }

    startTimer() {
      this.stopTimer();
      if (!document.hidden) this.timer = window.setInterval(() => this.update(), 1000);
    }

    stopTimer() {
      clearInterval(this.timer);
      this.timer = null;
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopTimer();
        this.cancelRefresh();
      } else {
        this.update();
        this.startTimer();
      }
    }
  }
  customElements.define(elementName, KiekkoKingiRestockArchive);
})();
